import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { AiOutlineLink, AiOutlineSearch } from "react-icons/ai";
import { RiRobot2Line } from "react-icons/ri";
import { HiOutlineDocumentSearch } from "react-icons/hi";
import { BsKeyboard } from "react-icons/bs";

import Loader from "@/components/loader";
import api from "@/services/api";

const MissionsAo = () => {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [generatedKeywords, setGeneratedKeywords] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    page: 1,
    per_page: 50,
  });
  const navigate = useNavigate();

  const fetchMissions = async (isLoadMore = false) => {
    try {
      setLoading(true);

      let endpoint = "/mission_ao/search";

      const { ok, data, has_more, keywords } = await api.post(endpoint, filters);
      if (!ok) throw new Error("Error fetching missions");

      setMissions(isLoadMore ? [...missions, ...data] : data);
      setHasMore(has_more);
      
      // Store the generated keywords if available
      if (keywords && !isLoadMore) {
        setGeneratedKeywords(keywords);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching missions");
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalCount = async () => {
    try {
      const { ok, data } = await api.get("/mission_ao/count");
      if (ok) {
        setTotalCount(data);
      }
    } catch (error) {
      console.error("Error fetching count:", error);
    }
  };

  // Fetch total count on component mount
  useEffect(() => {
    fetchTotalCount();
  }, []);

  // Effet pour recharger quand les filtres changent
  useEffect(() => {
    const isLoadMore = filters.page > 1;
    fetchMissions(isLoadMore);
  }, [filters.page, filters.search]);

  const handleLoadMore = () => {
    if (!loading) {
      setFilters((f) => ({ ...f, page: f.page + 1 }));
    }
  };

  const handleSearch = () => {
    setMissions([]); // Clear current results
    setGeneratedKeywords([]); // Clear previous keywords
    setFilters((f) => ({ ...f, page: 1, search: searchTerm }));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Appels d'Offres</h1>
        <p className="text-sm text-gray-600">Trouvez et gérez vos appels d'offres en un seul endroit</p>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="flex items-center gap-2 mb-5">
          <RiRobot2Line className="h-8 w-8 text-blue-600" />
          <div>
            <h2 className="font-semibold text-lg text-gray-900">Recherche intelligente</h2>
            <p className="text-sm text-gray-500">Notre intelligence artificielle trouve les appels d'offres qui correspondent à vos besoins</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <AiOutlineSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="Décrivez votre besoin..."
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              value={searchTerm}
            />
          </div>
          <button
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all flex items-center justify-center"
            onClick={handleSearch}
            disabled={loading}>
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Recherche...
              </span>
            ) : (
              "Rechercher"
            )}
          </button>
        </div>

        <div className="flex items-center justify-between mt-3">
          <div className="text-sm text-gray-600 font-medium">
            <span className="text-blue-600 font-semibold">{totalCount}</span> appels d'offres disponibles
          </div>
        </div>
      </div>

      {loading && missions.length === 0 ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : missions.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <div className="text-gray-500 mb-2">Aucun appel d'offres trouvé</div>
          <div className="text-sm text-gray-400">Essayez d'ajuster vos critères de recherche</div>
        </div>
      ) : (
        <div className="space-y-4">
          {missions.map((mission) => {
            const isPast = new Date(mission.closure_date) < new Date();
            return (
              <div
                key={mission._id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/rfp/search/${mission._id}`)}>
                <div className="p-5">
                  <div className="mb-4">
                    <div className="text-lg font-medium text-gray-900">{mission.name || mission.offer_name}</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(mission.sectors || []).map((sector) => (
                        <span key={sector} className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                          {sector}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center gap-4 text-sm mt-3 border-t pt-3">
                    <div className="flex-1">
                      <div className="text-gray-500 mb-1">Source</div>
                      <div className="font-medium">{mission.origin}</div>
                      {mission.organization && <div className="text-xs text-gray-500 mt-1">{mission.organization}</div>}
                    </div>

                    <div className="flex-1">
                      <div className="text-gray-500 mb-1">Date de clôture</div>
                      {mission.closure_date ? (
                        <div>
                          <span className={`inline-flex px-2 py-1 rounded ${isPast ? "text-red-700 bg-red-100" : "text-green-700 bg-green-100"}`}>
                            {new Date(mission.closure_date).toLocaleDateString("fr-FR", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                          {!isPast && (
                            <div className="text-xs text-gray-500 mt-1">{Math.ceil((new Date(mission.closure_date) - new Date()) / (1000 * 60 * 60 * 24))} jours restants</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500">Non spécifiée</span>
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="text-gray-500 mb-1">Actions</div>
                      <div className="flex items-center gap-4">
                        <a
                          target="_blank"
                          href={mission.url}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors"
                          onClick={(event) => event.stopPropagation()}>
                          <AiOutlineLink size={16} />
                          <span className="underline">Voir l'appel d'offre</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasMore && missions.length > 0 && (
        <div className="flex justify-center mt-8">
          <button
            className="px-6 py-2 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 rounded-lg shadow-sm transition-all flex items-center"
            onClick={handleLoadMore}
            disabled={loading}>
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Chargement...
              </span>
            ) : (
              "Charger plus"
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default MissionsAo;
